import { useState, useEffect, useMemo } from 'react'
import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import { useSelector } from 'react-redux'

import PageHeader from '../../../../shared/PageHeader'
import ReportFilters from '../../../../shared/ReportFilters'
import ReportJobsList from '../../../../shared/ReportJobsList'
import useAsyncReport from '../../../../hooks/useAsyncReport'
import { fetchData } from '../../../../helpers/fetchData'
import { practionerOptionsFormat } from '../../../../constants/VisitsConstants'
import {
  addItemWiseSummaryJob,
  updateItemWiseSummaryJob,
  removeItemWiseSummaryJob
} from '../../../../redux/slices/AppSlice'
import { validateOneMonthRange, getMinDate, getOneMonthMaxDate } from '../../../../utils/dateRangeValidation'

const ItemWiseSummaryReportAsync = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const jobs = useSelector(state => state.app.itemWiseSummaryJobs || [])

  const { filters, setFilters, resetFilters } = usePersistedFilters(
    'item_wise_summary_async_new',
    {
      start_date: moment().subtract(1, 'day').toDate(),
    end_date: moment().toDate(),
    department_id: '',
    practitioner_id: '',
    format: 'excel'
    }
  )

  const [departments, setDepartments] = useState([])
  const [practitioners, setPractitioners] = useState([])
  const [practitionerLoading, setPractitionerLoading] = useState(false)

  const { loading, downloadingJobs, startJob, downloadReport, removeJob, retryJob } = useAsyncReport({
    addJobAction: addItemWiseSummaryJob,
    updateJobAction: updateItemWiseSummaryJob,
    removeJobAction: removeItemWiseSummaryJob,
    createEndpoint: '/v3/reports/items/itemwisereportsummary/jobs',
    statusEndpoint: '/v3/reports/items/itemwisereportsummary/jobs',
    downloadEndpoint: '/v3/reports/items/itemwisereportsummary/jobs',
    fileNamePrefix: 'item_wise_summary_report',
    existingJobs: jobs,
    buildPayload: (filters, format) => ({
      start_date: moment(filters.start_date).format('YYYY-MM-DD'),
      end_date: moment(filters.end_date).format('YYYY-MM-DD'),
      department_id: filters.department_id || undefined,
      practitioner_id: filters.practitioner_id || undefined,
      format
    })
  })

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await fetchData('POST', '/v3/department/getList', {})
        if (res?.data) {
          setDepartments(res.data.map(d => ({ label: d.name_en, value: d.id })))
        }
      } catch (error) {
        console.log(error)
      }
    }
    loadDepartments()
  }, [])

  useEffect(() => {
    const loadPractitioners = async () => {
      setPractitionerLoading(true)
      try {
        if (!filters.department_id) {
          setPractitioners([])
          return
        }
        const response = await fetchData(
          'POST',
          `/v3/practitioner/getByDepartmentId?department_id=${filters.department_id}`,
          {}
        )
        setPractitioners(practionerOptionsFormat(response?.practitioners || response?.data || []))
      } catch (error) {
        console.error('Error loading practitioners:', error)
        setPractitioners([])
      } finally {
        setPractitionerLoading(false)
      }
    }
    loadPractitioners()
  }, [filters.department_id])

  const handleGenerate = async () => {
    if (!validateOneMonthRange(filters.start_date, filters.end_date, t)) return
    await startJob(filters, filters.format)
  }

  const handleReset = () => {
    resetFilters()
  }

  const handleChange = e => setFilters(prev => ({
    ...prev,
    [e.target.name]: e.target.value,
    ...(e.target.name === 'department_id' ? { practitioner_id: '' } : {})
  }))

  const filterConfig = useMemo(() => [
    {
      type: 'date',
      name: 'start_date',
      label: 'Start Date',
      width: 'w-[160px]'
    },
    {
      type: 'date',
      name: 'end_date',
      label: 'End Date',
      width: 'w-[160px]',
      minDate: getMinDate(filters.start_date),
      maxDate: getOneMonthMaxDate(filters.start_date)
    },
    {
      type: 'dropdown',
      name: 'department_id',
      label: 'Department',
      options: departments,
      placeholder: 'All',
      filter: true,
      width: 'w-[160px]'
    },
    {
      type: 'dropdown',
      name: 'practitioner_id',
      label: 'Practitioner (Optional)',
      options: practitioners,
      placeholder: 'All Practitioners',
      filter: true,
      disabled: !filters.department_id || practitionerLoading,
      width: 'w-[180px]'
    },
    {
      type: 'dropdown',
      name: 'format',
      label: 'Format',
      options: [
        { label: 'Excel', value: 'excel' },
        { label: 'PDF', value: 'pdf' }
      ],
      width: 'w-[160px]'
    }
  ], [departments, filters.start_date])

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('Item Wise Summary Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <ReportFilters
        filters={filters}
        onChange={handleChange}
        filterConfig={filterConfig}
        onGenerate={handleGenerate}
        onReset={handleReset}
        loading={loading}
      />

      <ReportJobsList
        jobs={jobs}
        onDownload={downloadReport}
        onRemove={removeJob}
        onRetry={retryJob}
        downloadingJobs={downloadingJobs}
        reportTitle={t('Item Wise Summary Report')}
      />
    </div>
  )
}

export default ItemWiseSummaryReportAsync
